import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import findCampanha from '../../service/findCampanha';
import inativarCampanha from '../../service/inativarCampanha';
import ativarCampanha from '../../service/ativarCampanha';
import { Button } from 'primereact/button';
import { SortOrder } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Toolbar } from 'primereact/toolbar';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

export default function CampanhaList({ onEdit, onVouchers }: { onEdit: (campanha: any) => void, onVouchers: (campanha: any) => void }) {
  const { t } = useTranslation();
  const [campanhas, setCampanhas] = useState(undefined);
  const [totalRecords, setTotalRecords] = useState(0);
  const [descricao, setDescricao] = useState<string>('');
  const toast = useRef<Toast>(null);
  const [lazyState, setlazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
    descricao: null as string | null,
    ativo: true as boolean | null,
    sortField: 'descricao',
    sortOrder: SortOrder.ASC
  });

  const normalizeDescricao = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  };

  const STATUS_TODOS = 'TODOS';
  const statusOptions = [
    { label: t('todos'), value: STATUS_TODOS },
    { label: t('ativo'), value: 'true' },
    { label: t('inativo'), value: 'false' },
  ];
  const statusDropdownValue = lazyState.ativo === null ? STATUS_TODOS : String(lazyState.ativo);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setlazyState(prev => ({ ...prev, first: 0, page: 1, descricao: normalizeDescricao(descricao) }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [descricao]);

  const changeStatus = (e: any) => {
    const novoAtivo = e.value === STATUS_TODOS ? null : e.value === 'true';
    setlazyState(prev => ({ ...prev, first: 0, page: 1, ativo: novoAtivo }));
  };

  const onPage = (event: any) => {
    setlazyState({
      first: event.first, rows: event.rows, page: (event.first / event.rows) + 1,
      descricao: normalizeDescricao(descricao), ativo: lazyState.ativo,
      sortField: event.sortField, sortOrder: event.sortOrder
    });
  };
  const onSort = (event: any) => {
    setlazyState({
      first: 0, rows: 10, page: 1, descricao: normalizeDescricao(descricao), ativo: lazyState.ativo,
      sortField: event.sortField, sortOrder: event.sortOrder
    });
  };

  const startContent = (
    <div className="filtros">
      <div className="filtros-cols">
        <div className="filtros-item">
          <label htmlFor="descricao" className="font-bold">{t('descricao')}</label>
          <InputText id="descricao" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder={t('pesquisar') + '...'} />
        </div>
        <div className="filtros-item">
          <label htmlFor="status" className="font-bold">Status</label>
          <Dropdown inputId="status" value={statusDropdownValue} options={statusOptions} optionValue="value" onChange={changeStatus} />
        </div>
      </div>
    </div>
  );

  const loadLazyData = useCallback(() => {
    findCampanha(lazyState).then(data => { setTotalRecords(data.total); setCampanhas(data.campanhas) })
      .catch(() => { setTotalRecords(0); setCampanhas(undefined) });
  }, [lazyState])

  const editBodyTemplate = (campanha: any) => (
    <Button icon="pi pi-pencil" tooltip={t('editar') as string} tooltipOptions={{ position: 'top' }} onClick={() => onEdit(campanha)} style={{ background: 'transparent', color: 'gray', border: 'none' }} />
  );

  const vouchersBodyTemplate = (campanha: any) => (
    <Button icon="pi pi-ticket" tooltip={t('vouchers') as string} tooltipOptions={{ position: 'top' }} onClick={() => onVouchers(campanha)} style={{ background: 'transparent', color: '#183462', border: 'none' }} />
  );

  const statusBodyTemplate = (campanha: any) => campanha.ativo === false
    ? <Tag icon="pi pi-pause-circle" severity="warning" value={t('inativo')} />
    : <Tag icon="pi pi-check-circle" severity="success" value={t('ativo')} />;

  const dataInclusaoBodyTemplate = (campanha: any) => campanha.dataInclusao
    ? <span>{new Date(campanha.dataInclusao).toLocaleDateString('pt-BR')}</span>
    : <span>-</span>;

  const executarAtivar = (campanha: any) => {
    ativarCampanha(campanha.id).then((mensagem) => {
      toast.current?.show({ severity: 'success', summary: t('sucesso'), detail: mensagem, life: 3000 });
      loadLazyData();
    }).catch((error) => toast.current?.show({ severity: 'error', summary: t('erro'), detail: error?.message || String(error), life: 5000 }));
  };

  const executarInativar = (campanha: any) => {
    inativarCampanha(campanha.id).then((mensagem) => {
      toast.current?.show({ severity: 'success', summary: t('sucesso'), detail: mensagem, life: 3000 });
      loadLazyData();
    }).catch((error) => toast.current?.show({ severity: 'error', summary: t('erro'), detail: error?.message || String(error), life: 5000 }));
  };

  const confirmarInativar = (campanha: any) => {
    confirmDialog({
      message: `${t('confirmarInativar')} "${campanha.descricao}"?`,
      header: t('inativar') as string,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('inativar') as string,
      rejectLabel: t('cancelar') as string,
      accept: () => executarInativar(campanha)
    });
  };

  const statusActionBodyTemplate = (campanha: any) => campanha.ativo === false
    ? <Button icon="pi pi-check-circle" tooltip={t('ativar') as string} tooltipOptions={{ position: 'top' }} onClick={() => executarAtivar(campanha)} style={{ background: 'transparent', color: 'gray', border: 'none' }} />
    : <Button icon="pi pi-ban" tooltip={t('inativar') as string} tooltipOptions={{ position: 'top' }} onClick={() => confirmarInativar(campanha)} style={{ background: 'transparent', color: '#e24c4c', border: 'none' }} />;

  useEffect(() => { loadLazyData(); }, [lazyState, loadLazyData])

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="mt-3">
        <Toolbar start={startContent} className="surface-card shadow-1 border-round-xl mb-4" />
        <DataTable value={campanhas} first={lazyState.first} dataKey="id" paginator={true} lazy={true}
          emptyMessage="Sem registros"
          onSort={onSort} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder}
          rows={lazyState.rows} totalRecords={totalRecords} onPage={onPage}
          tableStyle={{ minWidth: '75rem' }}>
          <Column field="descricao" sortable header={t('descricao')} />
          <Column field="descricaoPermissao" header={t('consulta')} />
          <Column field="quantidade" header={t('quantidade')} />
          <Column field="nomeFranquia" header={t('franquia')} />
          <Column field="nomeUsuarioInclusao" header={t('incluidoPor')} />
          <Column body={dataInclusaoBodyTemplate} header={t('dataInclusao')} />
          <Column body={statusBodyTemplate} header="Status" />
          <Column body={vouchersBodyTemplate} header={t('vouchers')} />
          <Column body={editBodyTemplate} />
          <Column body={statusActionBodyTemplate} />
        </DataTable>
      </div>
    </div>
  )
}
